/**
 * Planilha detalhada dos pedidos de produto da estação: carteira × cartão,
 * líquido de cada parte, alíquota e dados de entrega — colunas que só existem
 * para pedido de produto e por isso ficam fora da lista unificada de vendas.
 *
 * Era a exportação da antiga lista "Vendas Online"; o cálculo é o mesmo.
 */
import axios from 'axios';
import _ from 'lodash';
import * as XLSX from 'xlsx';
import { format, parseISO } from 'date-fns';
import { BASE_API, USER_TOKEN } from 'config/consts';

// Evita que o Excel interprete o valor como fórmula.
function sanitizeExcelValue(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  return /^[=+\-@\t]/.test(str) ? `'${str}` : str;
}

const virgula = (valor) => String(valor).replace('.', ',');

const CABECALHO = [
  'ID',
  'ID Pagar.me',
  'Atividade',
  'Data',
  'Nome',
  'CPF',
  'Produtos',
  'Valor final',
  'Valor pago da carteira',
  'Valor líquido da carteira',
  'Valor pago no cartão',
  'Valor líquido do cartão/PIX',
  'Alíquota (%)',
  'Status',
  'Método de pagamento',
  'Endereço',
  'Complemento',
  'Entrega',
];

function linhaDoPedido(item, list) {
  const transacoes = item.future_transactions || [];
  const futureTransaction = transacoes.find((t) => !t.gateway_or_wallet);
  const futureTransactionCreditCard = transacoes.find((t) => t.gateway_or_wallet);

  // Pedidos da mesma compra dividem o valor pago no cartão na proporção do
  // que cada um usou da carteira.
  const totalWalletValue = list
    .filter((outro) => outro.purchase_id === item.purchase_id)
    .reduce((soma, outro) => soma + parseFloat(outro.wallet_value), 0);

  // Um SingleProductOrder por unidade: agrupa por produto para "Nome x2".
  const produtos = [];
  (item.single_product_orders || []).forEach((spo) => {
    const idDoProduto = spo.product?.id;
    const indice = produtos.findIndex((p) => p.id === idDoProduto);
    if (indice >= 0) {
      produtos[indice].quantity += 1;
    } else {
      produtos.push({ id: idDoProduto, name: spo.product?.name, quantity: 1 });
    }
  });

  const valorBruto = parseFloat(item.final_value);
  const valorLiquido =
    (futureTransaction ? parseFloat(futureTransaction.value) : 0) +
    (futureTransactionCreditCard ? parseFloat(futureTransactionCreditCard.value) : 0);
  const aliquota =
    valorBruto > 0 && valorLiquido > 0
      ? `${virgula(((1 - valorLiquido / valorBruto) * 100).toFixed(2))}%`
      : '-';

  return [
    item.purchase_id,
    sanitizeExcelValue(item.gateway_charge ? item.gateway_charge.external_id : ''),
    sanitizeExcelValue(item.action ? item.action.name : ''),
    sanitizeExcelValue(format(parseISO(item.createdAt), 'dd-MM-yyyy HH:mm')),
    sanitizeExcelValue(item.user?.name),
    sanitizeExcelValue(item.user?.cpf),
    sanitizeExcelValue(produtos.map((p) => `${p.name} x${p.quantity}`).join('; ')),
    virgula(item.final_value),
    virgula(item.wallet_value),
    futureTransaction
      ? virgula(futureTransaction.value)
      : item.status === 'Pagamento recusado' ||
        item.wallet_value === '0.00' ||
        (item.gateway_charge?.payment_method === 'pix' && item.status === 'Expirado')
      ? '0,00'
      : '',
    totalWalletValue > 0
      ? virgula(
          _.round(
            parseFloat(item.total_credit_card_value) *
              (parseFloat(item.wallet_value) / totalWalletValue),
            2
          )
        )
      : virgula(item.total_credit_card_value),
    futureTransactionCreditCard
      ? virgula(futureTransactionCreditCard.value)
      : item.status === 'Pagamento recusado' || item.total_credit_card_value === '0.00'
      ? '0,00'
      : '',
    aliquota,
    sanitizeExcelValue(item.status),
    item.gateway_charge
      ? item.gateway_charge.payment_method === 'pix'
        ? 'PIX'
        : 'Cartão de crédito'
      : parseFloat(item.total_credit_card_value) > 0
      ? 'Cartão de crédito'
      : 'Somente carteira',
    sanitizeExcelValue(item.address || ''),
    sanitizeExcelValue(item.reference || ''),
    sanitizeExcelValue(item.delivery_location?.name || ''),
  ];
}

/**
 * Baixa a planilha dos pedidos de produto da estação no período (AAAA-MM-DD,
 * horário de Brasília). Sem datas, exporta o histórico inteiro. Devolve
 * quantos pedidos foram exportados.
 */
export async function exportarPedidosDetalhados({ stationId, startDate, endDate }) {
  const query = [`where[station_id]=${stationId}`, 'light=true'];
  if (startDate) query.push(`startDate=${startDate}`);
  if (endDate) query.push(`endDate=${endDate}`);
  const response = await axios.get(`${BASE_API}/product-orders/?${query.join('&')}`, {
    headers: { Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}` },
  });
  const list = response.data || [];
  const linhas = [CABECALHO, ...list.map((item) => linhaDoPedido(item, list))];
  const ws = XLSX.utils.aoa_to_sheet(linhas);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Pedidos');
  const sufixo = startDate || endDate ? `_${startDate || 'inicio'}_a_${endDate || 'hoje'}` : '';
  XLSX.writeFile(wb, `Pedidos_de_produto${sufixo}.xlsx`);
  return list.length;
}
